import * as Sentry from "@sentry/react";
import { useAlert } from "components/UI/alertSakeBar/alertProvider";
import QnALoader from "components/VocabQnAModal/QnALoader";
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { question_type_generate_more } from "utils/config/reference";
import { handleStreamResponse } from "utils/methods/QuestionCreateStreamHandle";
import { useUserProfileConfig } from "store/users/selectors";
import SentryWrap from 'components/Sentry';
interface Props {
    assignmentAiModal: boolean;
    setassignmentAiModal: (value: boolean) => void;
    page: string;
    chapter: string;
    pageSrc: string;
    content_type?: string;
    srt_url?:string;
}

interface Question {
    id?: number;
    question: string;
    answer: string;
}

interface APIResponse {
    success: boolean;
    data?: {
        title?: string;
        questions?: Question[];
    };
}

const InteractionsAssignmentModal = forwardRef(({ 
    assignmentAiModal,
    setassignmentAiModal, 
    page, 
    chapter, 
    pageSrc,  
    content_type ,
    srt_url 
}: Props, ref) => {
    const showAlert = useAlert();
    const [isQuestionModalOpen, setIsQuestionModalOpen] = useState<boolean>(false);
    const [ai_questions, setAiQuestions] = useState<Question[]>([]);
    const [loader, setLoader] = useState<boolean>(false);
    const [loaderMore, setLoaderMore] = useState<boolean>(false);
    const [title, setTitle] = useState<string>('');
    const [selectedTypes, setSelectedTypes] = useState<string[]>(
        question_type_generate_more.map(option => option.value)
    );
    const { isAiPermissionEnabled } = useUserProfileConfig();
    const [generatedCount,SetGenerateCount] = useState(0)
    const questionRequestIdRef = useRef(0);

    useEffect(() => {
        if (!isQuestionModalOpen) {
            questionRequestIdRef.current += 1;
            setAiQuestions([]);
            setLoader(false);
            setLoaderMore(false);
            SetGenerateCount(0);
        }
    }, [isQuestionModalOpen]);

    useEffect(() => {
        if (!assignmentAiModal) {
            setIsQuestionModalOpen(false);
        }
    }, [assignmentAiModal]);

    const handleGenerate = async (is_more?: boolean,lang?:any|string|null|undefined): Promise<void> => {
        if (!isAiPermissionEnabled) {
            showAlert('Please enable AI permission to generate questions.', 'error');
            return;
        }
        setLoader(!is_more);
        setLoaderMore(true);
        setassignmentAiModal(true);
        setIsQuestionModalOpen(true);
        const requestId = questionRequestIdRef.current + 1;
        questionRequestIdRef.current = requestId;
        
        try {
            const data = JSON.stringify({
                bookId: chapter,
                from_page: '',
                to_page: '',
                question_type: selectedTypes || [],
                resource: false,
                srt_url,
                question_language: lang?.toLowerCase()
            })
            await handleStreamResponse({
                path: "ai/generate/generate-questions-stream",
                body: data,
                onTitle: (title: any) => {
                    if (questionRequestIdRef.current !== requestId) return;
                    setTitle(title)
                    SetGenerateCount(0)
                },
                onQuestion: (question: any) => {
                    if (questionRequestIdRef.current !== requestId) return;
                    setAiQuestions(prev => [...prev, { ...question, id: Math.random() * 1000000000 }]);
                    SetGenerateCount(prev => prev + 1)
                    setLoader(false);
                    setLoaderMore(false);
                },
                onDone: () => {
                    if (questionRequestIdRef.current !== requestId) return;
                    setLoaderMore(false);
                    setLoader(false);
                    SetGenerateCount(0)
                },
                onError: () => {
                    if (questionRequestIdRef.current !== requestId) return;
                    setLoaderMore(false);
                    setLoader(false);
                    SetGenerateCount(0)
                }
            })
        } catch (error: any) {
            Sentry.captureException(`326 Error in InteractionsAssignmentModal: ${error}`);
            showAlert(error?.message || 'Something went wrong, please try with another page!','error');
            setassignmentAiModal(true);
            setIsQuestionModalOpen(false);
        } finally {
            setLoader(false);
            setLoaderMore(false);
        }
    };

    // 🔹 Exposing handleGenerate through ref
    useImperativeHandle(ref, () => ({
        generateQuestions: (lang:any|string|null|undefined)=>{
            handleGenerate(false,lang);
        }
    }));


    return (
        <SentryWrap><QnALoader
            assignmentAiModal={isQuestionModalOpen}
            setAssignmentAiModal={(value) => {
                if (!value) {
                    questionRequestIdRef.current += 1;
                    setAiQuestions([]);
                    setLoader(false);
                    setLoaderMore(false);
                    SetGenerateCount(0);
                }
                setassignmentAiModal(value);
                setIsQuestionModalOpen(value);
            }}
            handleGenerateMoreQnA={(topic,language) => handleGenerate(true,language)}
            generateQnALoader={loader}
            questionGenerator={ai_questions}
            page={page}
            assignmentTitle={title}
            chapter={chapter}
            pageSrc={pageSrc}
            loading={loaderMore}
            from={'ai_image'}
            selectedTypes={selectedTypes as never[]}
            setSelectedTypes={setSelectedTypes}
            qnaWord={undefined} 
            pageNumber={undefined}  
            generatedCount={generatedCount}
            setQuestionGenerator={setAiQuestions as any}
            uniqueKeyAssignmentAi={''}
            resourceKeyAssignmentAi={''}
        /></SentryWrap>
    );
});

export default InteractionsAssignmentModal;